import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import { DEPARTURE, fromLocalInput, toLocalInput } from "../../departure";
import { whenText } from "../../dayTime";
import DayTimePicker from "../../components/DayTimePicker";
import FieldRow from "../../components/FieldRow";

const useStyles = makeStyles({
  note: { margin: 0, fontSize: 12, color: T.mutedFg },
  danger: { margin: "4px 0 0", fontSize: 11, color: T.danger },
  check: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: T.fg,
    marginBottom: 12,
    cursor: "pointer",
  },
  checkbox: { width: 15, height: 15, accentColor: T.primary, flexShrink: 0 },
});

/**
 * When the guest expects to leave, and the exit date that follows from it.
 *
 * A pickup is now part of every booking the desk takes, so the picker is not
 * optional. The one way out is the checkbox, and only the cards that price from
 * the real exit - the hourly ones - can afford to offer it: every other card
 * sells a departure date, and a booking without one cannot be priced.
 */
export default function DepartureSection({
  departure,
  isFixedPackage,
  isSingleDay,
  showDates,
  endDay,
  setEndDay,
  tz,
  now,
  minIso,
  allowPast,
  allowUnknown,
}) {
  const s = useStyles();
  const ui = useUi();

  const {
    mode,
    at,
    select,
    options,
    minEndDay,
    hasMovableExitDate,
    scheduledExitLabel,
    outOfRange,
    overruns,
  } = departure;

  const unknown = allowUnknown && mode === DEPARTURE.DEFAULT;

  const checkbox = allowUnknown && (
    <label className={s.check}>
      <input
        type="checkbox"
        checked={unknown}
        onChange={(e) =>
          select(e.target.checked ? DEPARTURE.DEFAULT : DEPARTURE.SCHEDULED)
        }
        className={s.checkbox}
      />
      Departure not known yet
    </label>
  );

  if (unknown) {
    return (
      <FieldRow label="Departs" hint="The guest confirms at pickup.">
        {checkbox}
      </FieldRow>
    );
  }

  if (mode === DEPARTURE.SCHEDULED) {
    /* The hook holds venue wall-clock; the picker deals in instants. */
    const iso = fromLocalInput(at, tz);

    return (
      <FieldRow label="Departs" hint={whenText({ iso, tz, now })}>
        {checkbox}
        <DayTimePicker
          tz={tz}
          now={now}
          label="Departs"
          value={iso}
          onChange={(next) => departure.setAt(toLocalInput(next, tz))}
          minIso={minIso}
          allowPast={allowPast}
          /* A card that derives its exit from the entry only opens the clock. */
          dayCount={options.scheduledTimeOnly ? 1 : undefined}
          minReason="Before the arrival"
        />
        {outOfRange ? (
          <p className={s.danger}>That is past this booking's window.</p>
        ) : (
          overruns &&
          scheduledExitLabel && (
            <p className={s.note}>
              Booking is paid until {scheduledExitLabel}; the hours after that
              are charged as overstay.
            </p>
          )
        )}
      </FieldRow>
    );
  }

  /* The card fixes its own exit. All that is left to say is which date the
     booking is sold on, where the card lets that move at all. */
  return (
    <FieldRow
      label={isFixedPackage ? "Scheduled exit (fixed)" : "Scheduled exit"}
      htmlFor="qb-exit-day"
      hint={
        scheduledExitLabel
          ? `Set by this guest card — by ${scheduledExitLabel}.`
          : "Set by this guest card."
      }
    >
      {showDates && !isSingleDay ? (
        <input
          id="qb-exit-day"
          type="date"
          value={endDay}
          min={minEndDay}
          disabled={isFixedPackage || !hasMovableExitDate}
          onChange={(e) => setEndDay(e.target.value)}
          className={cx(ui.field)}
        />
      ) : (
        <p className={s.note}>The guest card sets when this booking closes.</p>
      )}
    </FieldRow>
  );
}
